import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context
const ThemeContext = createContext();

// Theme options
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

// Provider component
export function ThemeProvider({ children }) {
  // Initialize theme from localStorage or default to system preference
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || THEMES.SYSTEM;
  });
  
  // Track if we're using dark mode
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Apply the theme to the document
  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme', theme);
    
    // Determine if we should use dark mode
    let darkModeOn = false;
    
    if (theme === THEMES.DARK) {
      darkModeOn = true;
    } else if (theme === THEMES.SYSTEM) {
      // Check system preference
      darkModeOn = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    // Update state
    setIsDarkMode(darkModeOn);
    
    // Apply theme to document
    if (darkModeOn) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);
  
  // Listen for system preference changes
  useEffect(() => {
    if (theme !== THEMES.SYSTEM) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setIsDarkMode(e.matches);
      if (e.matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);
  
  // Set theme function
  const setThemePreference = (newTheme) => {
    if (Object.values(THEMES).includes(newTheme)) {
      setTheme(newTheme);
    }
  };
  
  // Toggle between light and dark
  const toggleTheme = () => {
    if (theme === THEMES.DARK) {
      setTheme(THEMES.LIGHT);
    } else {
      setTheme(THEMES.DARK);
    }
  };
  
  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDarkMode,
        setTheme: setThemePreference,
        toggleTheme
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use the theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}